<script setup lang="ts">
import { ReserveType } from '@utils/itinerary';

type Image = {
    mobile: {
        src: string;
        width: number;
        height: number;
    };
    desktop: {
        src: string;
        width: number;
        height: number;
    };
};
const searchFormStore = useSearchFormStore();
const prevReserve = ref<ReserveType>();
const timeout = ref<number>();
const backLoaded = ref(false);
const reserveType = computed(() => searchFormStore.reserveType);
const background = computed<Image>(() => {
    if (reserveType.value === ReserveType.Hotel) {
        return {
            mobile: {
                src: '/img/search-form/hotel-mobile.svg',
                width: 293,
                height: 68,
            },
            desktop: {
                src: '/img/search-form/hotel.svg',
                width: 1440,
                height: 301,
            },
        };
    } else {
        return {
            mobile: {
                src: '/img/search-form/mobile-background.svg',
                width: 360,
                height: 135,
            },
            desktop: {
                src: '/img/search-form/desktop-background.svg',
                width: 1648,
                height: 302,
            },
        };
    }
});
const vehicleImages = [
    {
        reserveType: ReserveType.Flight,
        image: {
            mobile: {
                src: 'flight-mobile',
                width: 293,
                height: 68,
            },
            desktop: {
                src: 'flight-desktop',
                width: 497,
                height: 114,
            },
        },
    },
    {
        reserveType: ReserveType.Train,
        image: {
            mobile: {
                src: 'train-mobile',
                width: 331,
                height: 42,
            },
            desktop: {
                src: 'train-desktop',
                width: 629,
                height: 79,
            },
        },
    },
    {
        reserveType: ReserveType.Bus,
        image: {
            mobile: {
                src: 'bus-mobile',
                width: 205,
                height: 58,
            },
            desktop: {
                src: 'bus-desktop',
                width: 327,
                height: 93,
            },
        },
    },
    {
        reserveType: ReserveType.Taxi,
        image: {
            mobile: {
                src: 'taxi-mobile',
                width: 99,
                height: 33,
            },
            desktop: {
                src: 'taxi-desktop',
                width: 224,
                height: 74,
            },
        },
    },
];
watch(reserveType, (_value, prevValue) => {
    if (timeout.value) {
        window.clearTimeout(timeout.value);
    }
    prevReserve.value = prevValue;
    timeout.value = window.setTimeout(() => {
        prevReserve.value = undefined;
    }, 2000);
});

const vehicleImage = computed(() =>
    vehicleImages.find(img => img.reserveType === reserveType.value)
);
</script>

<template>
    <div class="illustration-wrapper" :class="{ loaded: backLoaded }">
        <div class="blur-hash" />
        <div class="illustration-container" :class="{ loaded: backLoaded }">
            <div v-show="backLoaded">
                <picture v-if="vehicleImage">
                    <source
                        media="(min-width: 769px)"
                        :srcset="`/img/search-form/${vehicleImage.image.desktop.src}.svg`"
                        :width="vehicleImage.image.desktop.width"
                        :height="vehicleImage.image.desktop.height"
                    />
                    <img
                        alt="vehicle"
                        class="vehicle"
                        :class="[vehicleImage.reserveType]"
                        :src="`/img/search-form/${vehicleImage.image.desktop.src}.svg`"
                        :width="vehicleImage.image.desktop.width"
                        :height="vehicleImage.image.desktop.height"
                    />
                </picture>
                <ClientOnly>
                    <picture>
                        <source
                            media="(min-width: 769px)"
                            :srcset="background.desktop.src"
                            :width="background.desktop.width"
                            :height="background.desktop.height"
                        />
                        <img
                            alt="background"
                            class="background"
                            :src="background.desktop.src"
                            :width="background.desktop.width"
                            :height="background.desktop.height"
                            @load="backLoaded = true"
                        />
                    </picture>
                </ClientOnly>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@mixin vehicle-animation($vehicle, $top, $show-x, $max: 300%, $min: -300%) {
    &.#{$vehicle} {
        top: $top;
        transform: translateX($show-x);
        visibility: visible;
        animation: show-#{$vehicle} 500ms cubic-bezier(0.36, 1.01, 0.45, 1);
        transform: translateX($show-x);

        @keyframes show-#{$vehicle} {
            0% {
                transform: translateX($max);
            }
            100% {
                transform: translateX($show-x);
            }
        }
        @keyframes hide-#{$vehicle} {
            0% {
                transform: translateX($show-x);
            }
            100% {
                transform: translateX($min);
            }
        }
    }
}
.illustration-wrapper {
    position: relative;
    height: 300px;
    @include mobile-only {
        display: none;
    }
    flex-shrink: 0;
    background-color: usePalette();

    &.loaded {
        .blur-hash {
            opacity: 0;
        }
    }

    .blur-hash {
        top: 0;
        position: absolute;
        width: 100%;
        height: 100%;
        opacity: 1;
        transition: opacity 300ms ease-out;
        z-index: 15;
        background-image: url('data:image/jpeg;base64,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');
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
    }
}

.illustration-container {
    position: relative;
    width: 100%;
    height: 100%;
    max-width: 1648px;
    margin: 0 auto;
    overflow: hidden;
    background: linear-gradient(
        0deg,
        usePalette() -0.02%,
        usePalette($p-blue, $p-200) 66.02%
    );
}

.background {
    position: absolute;
    right: 50%;
    transform: translateX(50%);
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    z-index: 10;
}

.vehicle {
    position: absolute;
    visibility: hidden;
    z-index: 5;
    right: 50%;

    @include vehicle-animation('flight', 33%, 95%);
    @include vehicle-animation('train', 41%, 110%);
    @include vehicle-animation('bus', 40%, 126%);
    @include vehicle-animation('taxi', 47%, 179%);
}

@media (min-width: 1648px) {
    .illustration-container {
        &::before {
            position: absolute;
            top: 0;
            right: 0;
            content: ' ';
            width: 200px;
            height: 100%;
            background: linear-gradient(
                90deg,
                rgba($color: usePalette(), $alpha: 0) 0%,
                usePalette() 84.37%
            );
            z-index: 11;
        }

        &::after {
            position: absolute;
            top: 0;
            left: 0;
            content: ' ';
            width: 200px;
            height: 100%;
            background: linear-gradient(
                270deg,
                rgba($color: usePalette(), $alpha: 0) 0%,
                usePalette() 84.37%
            );
            z-index: 11;
        }
    }
}
</style>
